import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { apiRequest } from "@/lib/queryClient";
import {
  normalizeEpisodeChapters,
  type EpisodeChapter,
  type PodcastEpisodeVideoSource,
} from "@scroll/shared-utils";

export interface EpisodeInfo {
  id: number;
  title: string;
  audioUrl: string;
  artworkUrl?: string;
  podcastTitle?: string;
  podcastId?: number;
  durationMs?: number;
  description?: string;
  publishedAt?: string;
  videoSource?: PodcastEpisodeVideoSource;
  /** Normalized chapter list. playEpisode normalizes any input shape — legacy
   *  {timestamp, label} or Podcasting 2.0 JSON — before storing. */
  chapterMarkers?: EpisodeChapter[];
}

interface AudioPlayerState {
  currentEpisode: EpisodeInfo | null;
  isPlaying: boolean;
  isBuffering: boolean;
  positionMs: number;
  durationMs: number;
  playbackSpeed: number;
}

interface AudioPlayerActions {
  playEpisode: (episode: EpisodeInfo) => Promise<void>;
  pause: () => void;
  resume: () => void;
  seekTo: (positionMs: number) => void;
  skipForward: (seconds?: number) => void;
  skipBack: (seconds?: number) => void;
  setSpeed: (speed: number) => void;
  stop: () => void;
}

type AudioPlayerContextValue = AudioPlayerState & AudioPlayerActions;

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

export const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const PROGRESS_SAVE_INTERVAL = 10_000;
const SPEED_STORAGE_KEY = "scroll-playback-speed";

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentEpisode, setCurrentEpisode] = useState<EpisodeInfo | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(() => {
    const saved = localStorage.getItem(SPEED_STORAGE_KEY);
    return saved ? parseFloat(saved) : 1;
  });

  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const currentEpisodeRef = useRef<EpisodeInfo | null>(null);

  // Keep ref in sync for interval callback
  useEffect(() => {
    currentEpisodeRef.current = currentEpisode;
  }, [currentEpisode]);

  // Create audio element once
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "metadata";
    audioRef.current = audio;

    const onTimeUpdate = () => {
      setPositionMs(audio.currentTime * 1000);
    };
    const onDurationChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDurationMs(audio.duration * 1000);
      }
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onEnded = () => {
      setIsPlaying(false);
      // Save as completed
      const ep = currentEpisodeRef.current;
      if (ep) {
        saveProgress(ep.id, audio.duration * 1000, true);
      }
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("playing", onCanPlay);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("playing", onCanPlay);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
    };
  }, []);

  // Periodic progress save
  useEffect(() => {
    if (isPlaying && currentEpisode) {
      progressIntervalRef.current = setInterval(() => {
        const ep = currentEpisodeRef.current;
        const audio = audioRef.current;
        if (ep && audio) {
          saveProgress(ep.id, audio.currentTime * 1000, false);
        }
      }, PROGRESS_SAVE_INTERVAL);
    }
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [isPlaying, currentEpisode]);

  const playEpisode = useCallback(async (rawEpisode: EpisodeInfo) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Normalize chapters at the boundary so player state holds a clean array
    const normalizedChapters = normalizeEpisodeChapters(rawEpisode.chapterMarkers);
    const episode: EpisodeInfo = {
      ...rawEpisode,
      chapterMarkers: normalizedChapters.length > 0 ? normalizedChapters : undefined,
    };

    // If same episode, just resume
    if (currentEpisodeRef.current?.id === episode.id) {
      await audio.play();
      return;
    }

    audio.pause();
    audio.src = episode.audioUrl;
    audio.playbackRate = playbackSpeed;
    setCurrentEpisode(episode);
    setPositionMs(0);
    setDurationMs(episode.durationMs || 0);
    setIsBuffering(true);

    // Fetch saved progress and seek
    let savedPositionMs = 0;
    try {
      const res = await fetch(`/api/podcasts/episodes/${episode.id}/progress`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        if (data.positionMs && !data.completed) {
          savedPositionMs = data.positionMs;
          audio.currentTime = savedPositionMs / 1000;
        }
      }
    } catch {
      // Continue without saved progress
    }

    await audio.play();
    saveProgress(episode.id, savedPositionMs || 1, false, true);
  }, [playbackSpeed]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    audioRef.current?.play();
  }, []);

  const seekTo = useCallback((ms: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = ms / 1000;
      setPositionMs(ms);
    }
  }, []);

  const skipForward = useCallback((seconds = 15) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(audio.currentTime + seconds, audio.duration || 0);
    }
  }, []);

  const skipBack = useCallback((seconds = 15) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(audio.currentTime - seconds, 0);
    }
  }, []);

  const setSpeed = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    localStorage.setItem(SPEED_STORAGE_KEY, String(speed));
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      // Save final progress before stopping
      const ep = currentEpisodeRef.current;
      if (ep) {
        saveProgress(ep.id, audio.currentTime * 1000, false);
      }
      audio.pause();
      audio.src = "";
    }
    setCurrentEpisode(null);
    setIsPlaying(false);
    setPositionMs(0);
    setDurationMs(0);
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{
        currentEpisode,
        isPlaying,
        isBuffering,
        positionMs,
        durationMs,
        playbackSpeed,
        playEpisode,
        pause,
        resume,
        seekTo,
        skipForward,
        skipBack,
        setSpeed,
        stop,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) throw new Error("useAudioPlayer must be used within AudioPlayerProvider");
  return ctx;
}

function saveProgress(episodeId: number, positionMs: number, completed: boolean, clearContinueDismissal = false) {
  apiRequest("PUT", `/api/podcasts/episodes/${episodeId}/progress`, {
    positionMs: Math.round(positionMs),
    completed,
    clearContinueDismissal,
  }).catch(() => {
    // Silently ignore progress save failures
  });
}
